import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaPhoneAlt,
  FaFileAlt,
  FaVideo,
  FaUserGraduate,
  FaClock,
  FaExclamationCircle,
} from 'react-icons/fa';
import {
  fetchPublicServices,
  formatServicePriceLabel,
} from '../../data/bookableServices';
import { isPagePricingService } from '../../config/pagePricingServices';
import './css/ServicesPage.css';

const SERVICE_ICONS = {
  phone: FaPhoneAlt,
  zoom: FaVideo,
  defense: FaUserGraduate,
};

const getServiceIcon = (id) => SERVICE_ICONS[id] || FaFileAlt;

const isExternalLink = (url) => {
  if (!url) return false;
  return /^(https?:|mailto:|tel:)/i.test(url) || url.includes('wa.me');
};

const ServiceCtaButton = ({ service, isFeatured }) => {
  const className = `btn-book${isFeatured ? ' btn-fill' : ''}`;

  if (isPagePricingService(service)) {
    return (
      <Link to="/booking" state={{ service }} className={className}>
        اطلبي حسب عدد الصفحات
      </Link>
    );
  }

  if (service.ctaType === 'quote' && service.ctaLink) {
    const link = service.ctaLink.trim();
    if (isExternalLink(link)) {
      return (
        <a
          href={link}
          className={className}
          target="_blank"
          rel="noopener noreferrer"
        >
          اطلب تسعيرة
        </a>
      );
    }
    return (
      <Link to={link} className={className}>
        اطلب تسعيرة
      </Link>
    );
  }

  return (
    <Link to="/booking" state={{ service }} className={className}>
      احجزي موعدك
    </Link>
  );
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    fetchPublicServices()
      .then((list) => {
        if (!isMounted) return;
        setServices(list);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <div className="services-page" dir="rtl">
      <header className="services-header">
        <div className="container text-center relative-z">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            الخدمات والأسعار
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            استثمار ذكي يختصر عليكِ سنوات من الجهد والتشتت.
          </motion.p>
        </div>
      </header>

      <section className="services-grid-section">
        <div className="container">
          {loading ? (
            <div className="text-center py-5">جاري تحميل الخدمات...</div>
          ) : services.length === 0 ? (
            <div className="text-center py-5">لا توجد خدمات متاحة حالياً</div>
          ) : (
            <motion.div
              className="row g-4"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {services.map((service) => {
                const Icon = getServiceIcon(service.id);
                const features = Array.isArray(service.features)
                  ? service.features
                  : [];
                const priceLabel = formatServicePriceLabel(service);
                const isFeatured = !!service.featured;

                return (
                  <div className="col-lg-6 col-md-12" key={service.id}>
                    <motion.div
                      className={`service-card${isFeatured ? ' featured-card' : ''}`}
                      variants={cardVariants}
                    >
                      {isFeatured && (
                        <div className="badge-featured">الأكثر طلبًا</div>
                      )}
                      <div className="icon-wrapper">
                        <Icon />
                      </div>
                      <div className="card-content">
                        <div className="header-row">
                          <h3>{service.name}</h3>
                          {priceLabel && (
                            <span className="price-tag">{priceLabel}</span>
                          )}
                        </div>
                        {service.duration && (
                          <p className="duration">
                            <FaClock /> المدة: {service.duration}
                          </p>
                        )}
                        {service.description && (
                          <p className="description">{service.description}</p>
                        )}
                        {features.length > 0 && (
                          <ul className="feature-list">
                            {features.map((f, i) => (
                              <li key={i}>{f}</li>
                            ))}
                          </ul>
                        )}
                        <ServiceCtaButton
                          service={service}
                          isFeatured={isFeatured}
                        />
                      </div>
                    </motion.div>
                  </div>
                );
              })}
            </motion.div>
          )}
        </div>
      </section>

      <section className="policy-section">
        <div className="container">
          <div className="policy-box">
            <h3 className="policy-title">
              <FaExclamationCircle /> سياسات وتنبيهات هامة
            </h3>

            <div className="row">
              <div className="col-md-6">
                <h4>⏰ أوقات العمل</h4>
                <p>من الساعة 8:00 صباحًا إلى 08:00 مساًء</p>
                <p>يوميًا ما عدا يوم الجمعة</p>
                <p>يُرجى اختيار اليوم والوقت المناسب لكِ من التقويم</p>
                <p>يتم عقد اللقاء عبر برنامج Zoom</p>
                <p>
                  في حال وجود إزعاج، تشويش، أو ظرف طارئ، يتم تقدير ذلك دائمًا بما
                  يضمن جودة الجلسة
                </p>
                <p className="note">
                  <strong>تنويه:</strong>
                  <br />
                  ترتفع الأسعار في أوقات المساء ويوم الجمعة،
                  <br />
                  وفي شهر رمضان تكون قيمة الجلسة مختلفة حسب الخدمة المختارة.
                </p>
              </div>

              <div className="col-md-6">
                <h4>📝 شروط الخدمة</h4>
                <ul>
                  <li>
                    جميع الخدمات مخصصة <strong>للبنات فقط</strong>.
                  </li>
                  <li>الدفع مقدم (تحويل بنكي) لتأكيد الحجز.</li>
                  <li>المبلغ المدفوع غير قابل للاسترجاع.</li>
                  <li>التخصصات المقبولة: العلوم الإنسانية فقط.</li>
                </ul>
              </div>
            </div>

            <div className="payment-note text-center mt-4">
              <p>
                يتم الدفع عبر التحويل لمصرف الإنماء وإرسال الإيصال عبر الواتساب.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
